/**
 * leaveGuard.ts — общая дверь «можно ли уйти» для действий, которые уводят
 * человека со страницы НЕ переходом по адресу (WEB-2910, остаток WEB-2343).
 *
 * Зачем она, если есть `useBlocker` роутера. Блокировщик ловит смену адреса —
 * и только её. Смена проекта в шапке устроена иначе: сначала проект
 * переключается (запрос на сервер, текущий проект в хранилище кабинета), и
 * лишь затем меняется адрес. К моменту, когда сработал бы блокировщик, черновик
 * уже принадлежит чужому проекту — экран сбрасывает его молча (WEB-2354), а
 * «Сохранить» записал бы правки не туда. Поэтому такое действие обязано
 * спросить ДО себя: `if (!(await confirmLeave())) return;`.
 *
 * Экран с несохранёнными правками регистрирует здесь свой вопрос на время
 * жизни (`registerLeaveGuard` в эффекте, снятие — в его уборке). Вопрос
 * отвечает `true` — уходить можно (правок нет, сохранено или выброшено), либо
 * `false` — человек остаётся.
 *
 * Граница: дверь работает ровно для тех действий, которые её зовут. Сейчас это
 * переключатель проекта в боковом меню (`ProjectSwitcher.tsx`, все три его
 * пункта), смена организации (`OrgSwitcher.tsx`) и вход в только что созданный
 * проект (`ProjectSwitcher.tsx`, `handleWizardClose`, WEB-2912; создание из режима организации
 * проект не меняет и уводит адресом — его ловит блокировщик роутера), а также
 * переход к пополнению баланса организации проекта (`useOrgTopUp.ts`,
 * `goToBalance`, WEB-2312 / WEB-1895: если организация проекта не активна,
 * дверь сперва переключает организацию и сбрасывает проект).
 */
export type LeaveGuard = () => Promise<boolean>;

/** Зарегистрированные вопросы в порядке регистрации. Живёт одну загрузку страницы. */
const guards = new Set<LeaveGuard>();

/** Регистрирует вопрос экрана; возвращает функцию снятия. */
export function registerLeaveGuard(guard: LeaveGuard): () => void {
  guards.add(guard);
  return () => {
    guards.delete(guard);
  };
}

/**
 * Спрашивает все зарегистрированные экраны по очереди. Первый отказ
 * останавливает опрос. Сбой самой проверки читается как «остаться»: правки
 * человека дороже перехода, а повторить переход он может сам.
 */
export async function confirmLeave(): Promise<boolean> {
  for (const guard of Array.from(guards)) {
    try {
      if (!(await guard())) return false;
    } catch {
      return false;
    }
  }
  return true;
}

/**
 * Только для тестов: снять все вопросы. В бою модуль живёт одну загрузку
 * страницы; в прогоне память модуля переживает случаи (шрам WEB-2359), поэтому
 * сброс зовётся из `jest.setup.ts`.
 */
export function __resetLeaveGuardsForTests(): void {
  guards.clear();
}
